import { api, ApiResponse } from "@/lib/api";
import { clearAuthToken, persistAuthTokenFromResponse } from "@/lib/cookie-manager";
import { DeviceData, ListData, PublicListData, TransactionData, UserAuthResponse, UserVIPData } from "../entry/server/user";
import { Movie, WatchedMovieData, WatchListItemRequest } from "../entry/server/movie";
import { User } from "../entry/server/user";

function handleAuthResponse(response: ApiResponse<UserAuthResponse>): ApiResponse<UserAuthResponse> {
    if (response.data) {
        console.log("handleAuthResponse", response.data);
        persistAuthTokenFromResponse(response.data);
    }
    return response;
}

export const userService = {
    async login(username: string, password: string): Promise<ApiResponse<UserAuthResponse>> {
        var response = await api.post<UserAuthResponse>({ url: "login", body: { username: username, password: password } });
        console.log("login response", response);
        return handleAuthResponse(response);
    },

    async register(username: string, name: string, email: string, password: string, confirmPassword: string): Promise<ApiResponse<UserAuthResponse>> {
        var response = await api.post<UserAuthResponse>({
            url: "register",
            body: { username: username, name: name, email: email, password: password, confirm_password: confirmPassword },
        });
        return handleAuthResponse(response);
    },

    async sendResetPasswordOtp(username: string): Promise<ApiResponse<any>> {
        return api.get<any>({ url: "users/reset-password/send-otp-code", queryParams: { username } });
    },
    
    async resetPassword(username: string, code: string, password: string): Promise<ApiResponse<any>> {
        return api.post<any>({ url: "users/reset-password", body: { username, code, password } });
    },

    async getMe(): Promise<ApiResponse<{vip: UserVIPData, user: User, unread_notifications_count: number}>> {
        return api.get<{vip: UserVIPData, user: User, unread_notifications_count: number}>({ url: "users/me" });
    },

    async logout(): Promise<ApiResponse<any>> {
        var response = await api.get<any>({ url: "logout" });
        clearAuthToken();
        return response;
    },

    async createList(name: string, description: string, is_public: boolean): Promise<ApiResponse<ListData>> {
        return api.post<ListData>({ url: "user-lists", body: { name, description, is_public } });
    },

    async getLists(page: number): Promise<ApiResponse<PublicListData[]>> {
        return api.get<PublicListData[]>({ url: "user-lists", queryParams: { page: page.toString() } });
    },

    async getList(id: number): Promise<ApiResponse<ListData>> {
        return api.get<ListData>({ url: `user-lists/${id}` });
    },

    async updateList(id: number, name: string, description: string, is_public: boolean): Promise<ApiResponse<ListData>> {
        return api.put<ListData>({ url: `user-lists/${id}`, body: { name, description, is_public } });
    },

    async deleteList(id: number): Promise<ApiResponse<any>> {
        return api.delete<any>({ url: `user-lists/${id}` });
    },

    async getListMovies(id: number, page: number, limit: number): Promise<ApiResponse<Movie[]>> {
        return api.get<Movie[]>({ url: `user-lists/${id}/movies`, queryParams: { page: page.toString(), limit: limit.toString() } });
    },

    async addMovieToList(listId: number, movieId: number): Promise<ApiResponse<any>> {
        return api.post<any>({ url: `user-lists/${listId}/movies/${movieId}` });
    },

    async removeMovieFromList(listId: number, movieId: number): Promise<ApiResponse<any>> {
        return api.delete<any>({ url: `user-lists/${listId}/movies/${movieId}` });
    },

    async getPublicLists(page: number): Promise<ApiResponse<PublicListData[]>> {
        return api.get<PublicListData[]>({ url: "public-lists", queryParams: { page: page.toString() } });
    },

    async getPublicList(id: number): Promise<ApiResponse<PublicListData>> {
        return api.get<PublicListData>({ url: `public-lists/${id}` });
    },

    async updateProfile(username: string, name: string, email: string, phone: string, avatar: number): Promise<ApiResponse<User>> {
        return api.put<User>({ url: "users/profile", body: { username, name, email, phone, avatar } });
    },

    async updatePassword(oldPassword: string, newPassword: string): Promise<ApiResponse<any>> {
        return api.put<any>({ url: "users/password", body: { old_password: oldPassword, new_password: newPassword } });
    },

    async getDevices(): Promise<ApiResponse<DeviceData[]>> {
        return api.get<DeviceData[]>({ url: "users/devices" });
    },

    async deleteDevice(deviceToken: string): Promise<ApiResponse<any>> {
        return api.delete<any>({ url: `users/devices/${deviceToken}` });
    },

    async setPrimaryDevice(deviceToken: string, code: string): Promise<ApiResponse<any>> {
        return api.post<any>({
            url: `users/primary-device`,
            body: { code, device_token: deviceToken },
        });
    },

    async sendOtpForPrimaryDevice(): Promise<ApiResponse<any>> {
        return api.post<any>({ url: `users/primary-device/send-otp-code` });
    },

    async getTransactions(page: number): Promise<ApiResponse<TransactionData[]>> {
        return api.get<TransactionData[]>({ url: "users/transactions", queryParams: { page: page.toString() } });
    },
    async getWalletBalance(): Promise<ApiResponse<number>> {
        return api.get<number>({ url: "users/wallet-balance" });
    },

    async saveWatchTime(playingTime: string): Promise<ApiResponse<{score: number}>> {
        return api.post<{score: number}>({ url: "users/player/watch-time", body: { watch_time: playingTime } });
    },

    async saveWatchListItem(items: WatchListItemRequest[]): Promise<ApiResponse<any>> {
        return api.post<any>({ url: "users/player/watch-list", body: { items } });
    },

    async getWatchedMovies(type: "movie" | "series", page: number): Promise<ApiResponse<WatchedMovieData[]>> {
        return api.get<WatchedMovieData[]>({ url: `users/watched-movies/${type}`, queryParams: { page: page.toString() } });
    },

    async removeWatchedMovie(id: number): Promise<ApiResponse<any>> {
        return api.delete<any>({ url: `users/watched-movies/${id}` });
    },

    async getFavorites(page: number): Promise<ApiResponse<Movie[]>> {
        return api.get<Movie[]>({ url: "users/favorites", queryParams: { page: page.toString() } });
    },
};